import React, { useContext, useRef } from 'react'
import { ConnectionStatus } from '@/constants/ConnectionStatus'
import { OnlyEnemies } from '@/constants/OnlyEnemyConstants'
import {
  MAP_BOSS_PROBABILITIES,
  MAP_NAME1_LIST,
  MAP_NAME2_LIST,
  MAP_NAME3_LIST,
  MAP_TYPE_PROBABILITIES
} from '@/constants/TMapBasicConstants'
import LoadingIcon from '@/views/LoadingIcon'
import { APIContext } from '@/App'
import { Pagination } from '@/types/Types'
import { useModal } from '@/customHooks/useModal'
import LocationCodeDetailModal from '@/views/modal/LocationCodeDetailModal'
import TMapDetailModal from '@/views/modal/TMapDetailModal'

type Props = {
  pagination: Pagination
}

const ResultAreaView = ({ pagination }: Props) => {
  // #region フック

  const api = useContext(APIContext)

  // #endregion フック

  // #region スタイル

  const resultAreaStyle: React.CSSProperties = {
    width: '100%',
    height: '100%'
  }

  const resultStyle: React.CSSProperties = {
    width: 'calc(100vw - 50px)',
    height: 'calc(100vh - 565px)',
    border: 'solid 1px',
    display: 'flex',
    flexDirection: 'column'
  }

  const numOfItemsStyle: React.CSSProperties = {
    textAlign: 'right',
    marginRight: '10px'
  }

  const resultTableAreaStyle: React.CSSProperties = {
    height: '285px',
    backgroundColor: '#FFFFFF',
    border: 'solid 1px',
    overflowY: 'scroll'
  }

  const paginationStyle: React.CSSProperties = {
    margin: 'auto'
  }

  const firstPageButtonStyle: React.CSSProperties = {
    marginRight: '20px'
  }

  const previousButtonStyle: React.CSSProperties = {
    marginRight: '20px'
  }

  const nextButtonStyle: React.CSSProperties = {
    marginRight: '20px'
  }

  const lastPageButtonStyle: React.CSSProperties = {
    marginRight: '150px'
  }

  const jumpButton: React.CSSProperties = {
    marginLeft: '10px'
  }

  // #endregion スタイル

  const showResult = () => {
    if (api.status === ConnectionStatus.CONNECTING) {
      return <LoadingIcon />
    } else if (api.content.result_data) {
      return <ResultTableView resultData={api.content.result_data} />
    }
  }

  const isConnecting = api.status === ConnectionStatus.CONNECTING

  return (
    <div style={resultAreaStyle}>
      検索結果：
      <div style={resultStyle}>
        <div style={numOfItemsStyle}>
          <span>
            {`${typeof api.content.start_index === 'number' ? api.content.start_index + 1 : '--'}件目 ～ ` +
              `${typeof api.content.end_index === 'number' ? api.content.end_index + 1 : '--'}件目 ／ ` +
              `合計：${typeof api.content.result_length === 'number' ? api.content.result_length : '--'}件`}
          </span>
        </div>
        <div style={resultTableAreaStyle}>{showResult()}</div>
        <div style={paginationStyle}>
          <button
            style={firstPageButtonStyle}
            onClick={pagination.gotoFirst}
            disabled={!pagination.hasPrev() || isConnecting}
          >
            先頭
          </button>
          <button
            style={previousButtonStyle}
            onClick={pagination.gotoPrev}
            disabled={!pagination.hasPrev() || isConnecting}
          >
            前へ
          </button>
          <button
            style={nextButtonStyle}
            onClick={pagination.gotoNext}
            disabled={!pagination.hasNext() || isConnecting}
          >
            次へ
          </button>
          <button
            style={lastPageButtonStyle}
            onClick={pagination.gotoLast}
            disabled={!pagination.hasNext() || isConnecting}
          >
            最後
          </button>
          <span>
            ページ番号指定：
            <input
              type='number'
              min={1}
              max={pagination.getMaxPageCount()}
              value={pagination.pageText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                pagination.setPageText(parseInt(e.target.value, 10))
              }
              disabled={isConnecting || !api.content.result_data}
            />{' '}
            ／ {pagination.getMaxPageCount()}
            ページ中
          </span>
          <button
            style={jumpButton}
            onClick={pagination.jump}
            disabled={isConnecting || !api.content.result_data}
          >
            ジャンプ
          </button>
        </div>
      </div>
    </div>
  )
}

export default ResultAreaView

type ResultTableProps = {
  resultData: {
    map_rank?: string
    map_seed?: string
    map_type_index?: number
    floor_count?: number
    b1f_enemy_rank?: number
    boss_index?: number
    map_name1_index?: number
    map_name2_index?: number
    map_name3_index?: number
    map_level?: number
    places?: string
    box_s_count?: number
    box_a_count?: number
    box_b_count?: number
    box_c_count?: number
    box_d_count?: number
    box_e_count?: number
    box_f_count?: number
    box_g_count?: number
    box_h_count?: number
    box_i_count?: number
    box_total_count?: number
    is_no_enemy?: number
    is_only?: number
    is_few_enemy?: number
    only_enemy_id?: number
  }[]
}

const ResultTableView = ({ resultData }: ResultTableProps) => {
  // #region フック

  const locationCodeDetailModal = useModal()

  const locationCode = useRef(0)

  const tmapDetailModal = useModal()

  const mapRank = useRef(-1)

  const mapSeed = useRef(-1)

  const places = useRef<number[]>([])

  // #endregion フック

  // #region スタイル

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderSpacing: '0px'
  }

  const theadStyle: React.CSSProperties = {
    position: 'sticky',
    top: '0px',
    backgroundColor: '#FFFFFF'
  }

  const thStyle: React.CSSProperties = {
    border: 'solid 1px',
    borderBottom: 'solid 2px',
    backgroundColor: '#C1E4FF'
  }

  const tdStyle: React.CSSProperties = {
    border: 'solid 1px'
  }

  const red: React.CSSProperties = {
    color: '#FF0000',
    fontWeight: 'bold'
  }

  const blue: React.CSSProperties = {
    color: '#0000FF',
    fontWeight: 'bold'
  }

  const anchorStyle: React.CSSProperties = {
    color: 'blue'
  }

  // #endregion スタイル

  // #region イベント

  const onClickLocationCode = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    const code = e.currentTarget.getAttribute('data-map-code')
    locationCode.current = parseInt(code || '0', 10)
    locationCodeDetailModal.openModal()
  }

  const onClickDetail = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    mapRank.current = parseInt(e.currentTarget.getAttribute('data-map-rank') || '-1', 16)
    mapSeed.current = parseInt(e.currentTarget.getAttribute('data-map-seed') || '-1', 16)
    const v = e.currentTarget.getAttribute('data-places')
    if (v) {
      places.current = v.split(',').map((s) => parseInt(s, 10))
    }
    tmapDetailModal.openModal()
  }

  // #endregion イベント

  const head = (
    <thead style={theadStyle}>
      <tr>
        <th style={thStyle}>Rank</th>
        <th style={thStyle}>Seed</th>
        <th style={thStyle}>地図名</th>
        <th style={thStyle}>場所候補</th>
        <th style={thStyle}>地形</th>
        <th style={thStyle}>深さ</th>
        <th style={thStyle}>B1F敵Rank</th>
        <th style={thStyle}>ボス</th>
        <th style={thStyle}>S箱</th>
        <th style={thStyle}>A箱</th>
        <th style={thStyle}>B箱</th>
        <th style={thStyle}>C箱</th>
        <th style={thStyle}>D箱</th>
        <th style={thStyle}>E箱</th>
        <th style={thStyle}>F箱</th>
        <th style={thStyle}>G箱</th>
        <th style={thStyle}>H箱</th>
        <th style={thStyle}>I箱</th>
        <th style={thStyle}>宝総数</th>
        <th style={thStyle}>敵無</th>
        <th style={thStyle}>オンリー</th>
        <th style={thStyle}>敵減</th>
        <th style={thStyle}>オンリーモンスター</th>
        <th style={thStyle}>詳細</th>
      </tr>
    </thead>
  )

  const list = resultData?.map((s) => (
    <tr key={`${s.map_rank}-${s.map_seed}`} className='result_row'>
      <td style={tdStyle} align='center'>
        {typeof s.map_rank === 'string' ? '0x' + s.map_rank : '取得失敗'}
      </td>
      <td style={tdStyle} align='center'>
        {typeof s.map_seed === 'string' ? '0x' + s.map_seed : '取得失敗'}
      </td>
      <td style={tdStyle}>
        {typeof s.map_name1_index === 'number' &&
        typeof s.map_name2_index === 'number' &&
        typeof s.map_name3_index === 'number' &&
        typeof s.map_level === 'number'
          ? MAP_NAME1_LIST[s.map_name1_index] +
            MAP_NAME2_LIST[s.map_name2_index] +
            MAP_NAME3_LIST[s.map_name3_index] +
            'Lv' +
            s.map_level
          : '取得失敗'}
      </td>
      <td style={tdStyle}>
        {typeof s.places === 'string'
          ? JSON.parse(s.places).map((t: number) => (
              <React.Fragment key={t}>
                <a href='#' style={anchorStyle} data-map-code={t} onClick={onClickLocationCode}>
                  {'0x' + t.toString(16).toLocaleUpperCase().padStart(2, '0')}
                </a>
                &nbsp;
              </React.Fragment>
            ))
          : '取得失敗'}
      </td>
      <td style={tdStyle} align='center'>
        {typeof s.map_type_index === 'number' ? MAP_TYPE_PROBABILITIES[s.map_type_index].name : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.floor_count === 'number' ? s.floor_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.b1f_enemy_rank === 'number' ? s.b1f_enemy_rank : '取得失敗'}
      </td>
      <td style={tdStyle}>
        {typeof s.boss_index === 'number' ? MAP_BOSS_PROBABILITIES[s.boss_index].name : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_s_count === 'number' ? s.box_s_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_a_count === 'number' ? s.box_a_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_b_count === 'number' ? s.box_b_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_c_count === 'number' ? s.box_c_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_d_count === 'number' ? s.box_d_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_e_count === 'number' ? s.box_e_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_f_count === 'number' ? s.box_f_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_g_count === 'number' ? s.box_g_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_h_count === 'number' ? s.box_h_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_i_count === 'number' ? s.box_i_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='right'>
        {typeof s.box_total_count === 'number' ? s.box_total_count : '取得失敗'}
      </td>
      <td style={tdStyle} align='center'>
        {typeof s.is_no_enemy !== 'number' ? (
          '取得失敗'
        ) : s.is_no_enemy === 1 ? (
          <span style={blue}>有</span>
        ) : (
          '無'
        )}
      </td>
      <td style={tdStyle} align='center'>
        {typeof s.is_only !== 'number' ? '取得失敗' : s.is_only === 1 ? <span style={blue}>有</span> : '無'}
      </td>
      <td style={tdStyle} align='center'>
        {typeof s.is_few_enemy !== 'number' ? (
          '取得失敗'
        ) : s.is_few_enemy === 1 ? (
          <span style={blue}>有</span>
        ) : (
          '無'
        )}
      </td>
      <td style={tdStyle}>
        {typeof s.only_enemy_id !== 'number' ? (
          '取得失敗'
        ) : s.only_enemy_id !== 0 ? (
          <span style={red}>{OnlyEnemies[s.only_enemy_id]}</span>
        ) : (
          OnlyEnemies[s.only_enemy_id]
        )}
      </td>
      <td style={tdStyle} align='center'>
        <a
          href='#'
          style={anchorStyle}
          data-map-rank={`${s.map_rank}`}
          data-map-seed={`${s.map_seed}`}
          data-places={JSON.parse(s.places || '')}
          onClick={onClickDetail}
        >
          詳細
        </a>
      </td>
    </tr>
  ))

  const body = <tbody>{list}</tbody>

  return (
    <>
      <table style={tableStyle}>
        {head}
        {body}
      </table>
      <LocationCodeDetailModal
        isOpen={locationCodeDetailModal.isOpen}
        closeModal={locationCodeDetailModal.closeModal}
        mapCode={locationCode.current}
      />
      <TMapDetailModal
        isOpen={tmapDetailModal.isOpen}
        closeModal={tmapDetailModal.closeModal}
        mapRank={mapRank.current}
        mapSeed={mapSeed.current}
        places={places.current}
      />
    </>
  )
}
